/* eslint-disable react-refresh/only-export-components */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ONBOARDING_STEP_KEYS,
  type OnboardingStepKey,
} from '../steps';
import { type TutorProfileResponse } from '@/lib/schemas/tutor-profile';

export const selectClass =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100';

export const textareaClass =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500';

export interface StepProps {
  profile: TutorProfileResponse | null;
  accessToken: string;
  onSaved: (next: TutorProfileResponse) => void;
  onError: (err: unknown) => void;
}

export function withCompletedStep(
  profile: TutorProfileResponse | null,
  key: OnboardingStepKey,
): OnboardingStepKey[] {
  const set = new Set<string>(profile?.completedSteps ?? []);
  set.add(key);
  return ONBOARDING_STEP_KEYS.filter((k) => set.has(k));
}

export function Field({
  id,
  label,
  value,
  onChange,
  helper,
  type = 'text',
  ...rest
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  helper?: string;
  type?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'id'>) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  );
}

export function ChipToggle({ label, selected, onToggle }: { label: string; selected: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={
        selected
          ? 'inline-flex h-9 items-center rounded-full bg-brand-600 px-3.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700'
          : 'inline-flex h-9 items-center rounded-full border border-slate-300 bg-white px-3.5 text-sm font-medium text-slate-700 transition-colors hover:border-brand-400 hover:text-brand-600 dark:border-slate-600 dark:bg-slate-700/60 dark:text-slate-300 dark:hover:border-brand-500 dark:hover:text-brand-400'
      }
    >
      {label}
    </button>
  );
}

export function FooterButtons({
  primaryLabel,
  busy,
  secondaryLabel,
  onSecondaryClick,
  secondaryVariant = 'outline',
}: {
  primaryLabel: string;
  busy: boolean;
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
  secondaryVariant?: 'outline' | 'link';
}) {
  return (
    <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-700">
      {secondaryLabel && onSecondaryClick && (
        secondaryVariant === 'link' ? (
          <button
            type="button"
            disabled={busy}
            onClick={onSecondaryClick}
            className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 underline cursor-pointer mr-3 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {secondaryLabel}
          </button>
        ) : (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={onSecondaryClick}
          >
            {secondaryLabel}
          </Button>
        )
      )}
      <Button type="submit" disabled={busy} className="min-w-[140px]">
        {busy ? 'Saving…' : primaryLabel}
      </Button>
    </div>
  );
}
